import React from 'react';
import { useMutation } from '@apollo/client';
import {
  ADD_ITEM_TO_SHOP_CART,
  REMOVE_ITEM_FROM_SHOP_CART,
} from '../graphql/mutation';
import { generateShopItems, getOrderItem } from '../lib';
import {
  ShopCart,
  OrderSubscription,
  OrderBenefit,
  OrderProduct,
  ShopCartItem,
  Client,
} from '../models';
import { useNotify } from './useNotify';
import { useShopCart } from './useShopCart';

export type TEditShopCartInput = {
  productType: 'product' | 'benefit' | 'plan';
  shopItem: ShopCartItem;
  client: string;
  newQuantity: number;
  benefitValue?: number;
};

// consulte el input de addItemToShopCart para estar mas famaliarizado con el shopCart
// Se requiere a partir del shopcart rconstruir el input para agregar al carrito que consiste en shopItems
export function useEditShopCart(shopCart: ShopCart) {
  const notify = useNotify();

  const [_, setShopCart] = useShopCart();

  const [loading, setLoading] = React.useState(false);

  const [productsShopItems, setProductsShopItems] = React.useState<
    ShopCartItem[]
  >(generateShopItems(shopCart, 'product'));

  const [benefitsShopItems, setBenefitsShopItems] = React.useState<
    ShopCartItem[]
  >(generateShopItems(shopCart, 'benefit'));

  const [plansShopItems, setPlansShopItems] = React.useState<ShopCartItem[]>(
    generateShopItems(shopCart, 'plan')
  );

  const [removeItemFromShopCartMutation] = useMutation<{
    removeItemFromShopCart: ShopCart;
  }>(REMOVE_ITEM_FROM_SHOP_CART);

  const [addItemToShopCartMutation] = useMutation<{
    addItemToShopCart: ShopCart;
  }>(ADD_ITEM_TO_SHOP_CART);

  React.useEffect(
    function syncShopCart() {
      setProductsShopItems(generateShopItems(shopCart, 'product'));
      setBenefitsShopItems(generateShopItems(shopCart, 'benefit'));
      setPlansShopItems(generateShopItems(shopCart, 'plan'));
    },
    [shopCart]
  );

  // editar las cantidades de los items del carrito
  async function editShopCart({
    productType,
    shopItem,
    client,
    newQuantity,
    benefitValue = 0,
  }: TEditShopCartInput) {
    if (loading) return;
    setLoading(true);

    let shopItems: ShopCartItem[];
    let searchKey = '';
    // determinar que tipo de producto es y por cual key se va a buscar
    if (productType === 'product') {
      shopItems = productsShopItems;
      searchKey = 'variant';
    } else if (productType === 'benefit') {
      shopItems = benefitsShopItems;
      searchKey = 'benefit';
    } else if (productType === 'plan') {
      shopItems = plansShopItems;
      searchKey = 'plan';
    }

    // buscar ese producto entre todos los items
    const shopItemIndex = shopItems.findIndex(
      (_shopItem) => _shopItem[searchKey] === shopItem[searchKey]
    );
    // obtener la lista de clientes que tienen ese producto
    const { shopList } = shopItems[shopItemIndex];
    // buscar el cliente al cual se le va editar la cantidad

    const clientItemIndex = shopList.findIndex(
      (_clientItem) => (_clientItem.client as unknown as Client)._id === client
    );

    // obtener el item del carrito de compras
    const orderItem = getOrderItem(
      shopItem,
      shopList[clientItemIndex],
      productType,
      shopCart
    );

    if (!orderItem?._id) {
      return notify(
        'No hemos encontrado ese producto en tu carrito de compras',
        'error'
      );
    }

    const currentQuantity = shopList[clientItemIndex].quantity;

    // si la nueva cantidad es mayor a la actual se agrega al carrito
    if (newQuantity > currentQuantity) {
      await addToShopCart(orderItem, productType);
    } else if (newQuantity < currentQuantity) {
      // si la nueva cantidad es menor a la actual se elimina del carrito
      await removeItemFromShopCart(orderItem);
    }

    setLoading(false);
  }

  async function addToShopCart(
    orderItem: OrderProduct | OrderBenefit | OrderSubscription,
    productType: 'product' | 'benefit' | 'plan'
  ) {
    try {
      const { data } = await addItemToShopCartMutation({
        variables: {
          data: {
            shopCart: shopCart._id,
            items: {
              benefit:
                productType === 'benefit'
                  ? (orderItem as OrderBenefit).benefit?._id
                  : null,
              variant:
                productType === 'product'
                  ? (orderItem as OrderProduct).variant?._id
                  : null,

              shopList: {
                client: (orderItem?.client as Client)?._id,
                quantity: 1,
                benefitPrice:
                  productType === 'benefit' ? orderItem.price : null,
              },
            },
          },
        },
      });
      setShopCart(data.addItemToShopCart);
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  }

  async function removeItemFromShopCart(orderItem) {
    try {
      const { data } = await removeItemFromShopCartMutation({
        variables: {
          data: {
            shopCart: shopCart._id,
            items: [orderItem._id],
          },
        },
      });
      setShopCart(data.removeItemFromShopCart);
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  }

  return {
    productsShopItems,
    benefitsShopItems,
    plansShopItems,
    editShopCart,
    loading,
  };
}
